import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, unref as _unref, resolveComponent as _resolveComponent, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, isRef as _isRef, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers } from "vue"

import moment from "moment";
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import ReportType from "@/core/enums/ReportTypes.enums";
import { formatMonthQuery } from "@/core/helpers/bao-cao-pdf-params";
import { openBaoCaoPdf } from "@/components/shared/pdf-viewer/open-bao-cao-pdf";
import { swalErrNotification, downloadBase64File } from "@/core/helpers/utils";
import { setBreadcrumbLinks } from "@/core/helpers/breadcrumb";
import { useCreatingForm } from "@/core/hooks/use-creating-form";
import { useDateTime } from "@/core/hooks/use-date-time-hook";
import { useLoading } from "@/core/hooks/use-loading";
import { usePagination } from "@/core/hooks/use-pagination";
import { useButtonCustom } from "@/core/hooks/use-button-custom";
import useFormInput from "@/core/hooks/use-form-input";
import {
  IDanhMucVatTuResponseBase,
  INhapKhoVatTuPhuTungMetaResponseBase,
  INhapKhoVatTuPhuTungRowResponseBase,
} from "@/core/interfaces/ApiResponses";
import { BaoCaoService } from "@/core/services/BaoCao.service";
import DanhMucVatTuService from "@/core/services/DanhMucVatTu.service";
import ButtonCustom from "@/components/shared/buttons/ButtonCustom.vue";
import DivCol from "@/layout/_shared/DivCol.vue";
import DivRow from "@/layout/_shared/DivRow.vue";
import LabelCustom from "@/components/shared/label-custom/LabelCustom.vue";
import PageContentBase from "@/layout/_shared/PageContentBase.vue";
import PageContentSection from "@/layout/_shared/PageContentSection.vue";
import Pagination from "@/components/shared/pagination/Pagination.vue";
import NhapKhoVatTuPhuTungTable from "./table-components/NhapKhoVatTuPhuTungTable.vue";
import CompanyReportTableWrapper from "./table-components/CompanyReportTableWrapper.vue";
import NhapKhoVatTuPhuTungPrintModal from "./print-modals/NhapKhoVatTuPhuTungPrintModal.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'NhapKhoVatTuPhuTung',
  setup(__props) {

setBreadcrumbLinks([{ text: "Báo cáo" }, { text: "Tình hình nhập kho phụ tùng" }]);
document.title = "Tình hình nhập kho phụ tùng - Đường sắt Hà Nội";

const store = useStore();
const router = useRouter();
const currentYear = computed(() => store.getters.getSelectedYear);
const { disableDate, defaultDate } = useDateTime();
const { formRef } = useCreatingForm();
const { startLoading, endLoading } = useLoading();
const { ButtonsType, ButtonTypeColors } = useButtonCustom();
const { currentPage, totalItems, searchTerm, perPage } = usePagination();
const { focusFirstErrInputV2 } = useFormInput();

const form = ref({
  from: defaultDate.value,
  to: defaultDate.value,
  id_danh_muc_vat_tu: undefined as number | undefined,
  hideZeroActivity: true,
});

const isFiltersChanging = ref(true);
const supportsPagination = ref(false);
const items = ref<INhapKhoVatTuPhuTungRowResponseBase[]>([]);
const reportMeta = ref<INhapKhoVatTuPhuTungMetaResponseBase | null>(null);
const dmOptions = ref<IDanhMucVatTuResponseBase[]>([]);
const searchingDm = ref(false);
const printModalRef = ref();

const openPdfViewer = () => {
  if (!form.value.id_danh_muc_vat_tu) return;

  openBaoCaoPdf(router, {
    reportType: ReportType.NHAP_KHO_VAT_TU_PHU_TUNG,
    from: formatMonthQuery(new Date(form.value.from)),
    to: formatMonthQuery(new Date(form.value.to)),
    year: Number(currentYear.value),
    idDmVatTu: form.value.id_danh_muc_vat_tu,
    nhomVatTu: selectedNhomVatTuLabel.value,
    search: searchTerm.value || undefined,
    hideZeroActivity: form.value.hideZeroActivity || undefined,
  });
};

const syncMonthRange = (changed: "from" | "to") => {
  const { from, to } = form.value;
  if (!from || !to) return;

  if (moment(from).isAfter(to, "month")) {
    if (changed === "from") {
      form.value.to = new Date(from);
    } else {
      form.value.from = new Date(to);
    }
  }
};

watch(
  () => form.value.from,
  () => {
    syncMonthRange("from");
  }
);

watch(
  () => form.value.to,
  () => {
    syncMonthRange("to");
  }
);

const fromDateValidation = (_: unknown, value: Date, callback: (e?: Error) => void) => {
  if (!value) {
    callback(new Error("Bắt buộc"));
    return;
  }

  callback();
};

const toDateValidation = (_: unknown, value: Date, callback: (e?: Error) => void) => {
  if (!value) {
    callback(new Error("Bắt buộc"));
    return;
  }

  callback();
};

const rules = ref({
  from: [{ validator: fromDateValidation, trigger: "change" }],
  to: [{ validator: toDateValidation, trigger: "change" }],
  id_danh_muc_vat_tu: [
    {
      required: true,
      type: "number",
      message: "Bắt buộc",
      trigger: "change",
    },
  ],
});

const selectedNhomVatTuLabel = computed(() => {
  if (!form.value.id_danh_muc_vat_tu) return "";
  return (
    dmOptions.value.find((d) => d.id === form.value.id_danh_muc_vat_tu)
      ?.ten_danh_muc ?? ""
  );
});

const showPrintButton = computed(
  () => !isFiltersChanging.value && items.value.length > 0
);

const searchDm = async (query: string) => {
  if (!query) {
    dmOptions.value = [];
    searchingDm.value = false;
    return;
  }

  searchingDm.value = true;
  try {
    const {
      data: {
        data: { data },
      },
    } = await DanhMucVatTuService.list(1, 200, query);
    dmOptions.value = data;
  } catch {
    dmOptions.value = [];
  } finally {
    searchingDm.value = false;
  }
};

const fetchBaoCao = async () => {
  if (!form.value.id_danh_muc_vat_tu) return;

  startLoading();
  try {
    const {
      data: { data },
    } = await BaoCaoService.getNhapKhoVatTuPhuTung({
      startMonth: new Date(form.value.from).getMonth() + 1,
      endMonth: new Date(form.value.to).getMonth() + 1,
      idDanhMucVatTu: form.value.id_danh_muc_vat_tu,
      ...(supportsPagination.value ? { page: currentPage.value } : {}),
      search: searchTerm.value || undefined,
      hideZeroActivity: form.value.hideZeroActivity || undefined,
    });

    items.value = data.rows ?? [];
    reportMeta.value = data.meta;
    supportsPagination.value = BaoCaoService.isNhapKhoVatTuPhuTungPaginated(data);

    const pagination = data.meta.pagination;
    if (supportsPagination.value && pagination) {
      totalItems.value = pagination.total;
      currentPage.value = pagination.current_page;
      perPage.value = pagination.per_page;
    } else {
      totalItems.value = 0;
      currentPage.value = 1;
    }
  } catch (e) {
    swalErrNotification(e, "Có lỗi xảy ra");
  } finally {
    endLoading();
  }
};

const generateBaoCao = () => {
  if (!formRef.value) return;
  formRef.value.validate(async (isValid) => {
    if (!isValid) {
      await focusFirstErrInputV2();
      return;
    }

    currentPage.value = 1;
    supportsPagination.value = false;
    await fetchBaoCao();
    isFiltersChanging.value = false;
  });
};

const onPageChange = async (page: number) => {
  currentPage.value = page;
  await fetchBaoCao();
};

const exportExcel = () => {
  if (!formRef.value) return;

  formRef.value.validate(async (isValid) => {
    if (!isValid) {
      await focusFirstErrInputV2();
      return;
    }

    startLoading();

    const idDanhMucVatTu = form.value.id_danh_muc_vat_tu;
    if (!idDanhMucVatTu) {
      endLoading();
      return;
    }

    try {
      const {
        data: {
          data: { filename, file_data, mime_type },
        },
      } = await BaoCaoService.exportNhapKhoVatTuPhuTung({
        startMonth: new Date(form.value.from).getMonth() + 1,
        endMonth: new Date(form.value.to).getMonth() + 1,
        idDanhMucVatTu,
        search: searchTerm.value || undefined,
        hideZeroActivity: form.value.hideZeroActivity || undefined,
        filterLabel: selectedNhomVatTuLabel.value || undefined,
      });

      await downloadBase64File(file_data, mime_type, filename);
    } catch (e) {
      swalErrNotification(e, "Có lỗi khi xuất Excel");
    } finally {
      endLoading();
    }
  });
};

watch(
  [form, searchTerm],
  () => {
    isFiltersChanging.value = true;
    supportsPagination.value = false;
  },
  { deep: true, immediate: true }
);

return (_ctx: any,_cache: any) => {
  const _component_el_date_picker = _resolveComponent("el-date-picker")!
  const _component_el_form_item = _resolveComponent("el-form-item")!
  const _component_el_col = _resolveComponent("el-col")!
  const _component_el_option = _resolveComponent("el-option")!
  const _component_el_select = _resolveComponent("el-select")!
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_checkbox = _resolveComponent("el-checkbox")!
  const _component_el_row = _resolveComponent("el-row")!
  const _component_el_form = _resolveComponent("el-form")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(NhapKhoVatTuPhuTungPrintModal, {
      ref_key: "printModalRef",
      ref: printModalRef,
      items: items.value,
      "don-vi": reportMeta.value?.don_vi ?? [],
      "from-month": reportMeta.value?.start_month ?? null,
      "to-month": reportMeta.value?.end_month ?? null,
      year: reportMeta.value?.year ?? currentYear.value,
      "nhom-vat-tu": selectedNhomVatTuLabel.value,
      onClose: _cache[0] || (_cache[0] = ($event: any) => (printModalRef.value.isOpen = false))
    }, null, 8, ["items", "don-vi", "from-month", "to-month", "year", "nhom-vat-tu"]),
    _createVNode(PageContentBase, { plain: "" }, {
      default: _withCtx(() => [
        _createVNode(PageContentSection, { "always-expanded": true }, {
          header: _withCtx(() => [
            _createTextVNode("Tình hình nhập kho phụ tùng")
          ]),
          default: _withCtx(() => [
            _createVNode(_component_el_form, {
              ref_key: "formRef",
              ref: formRef,
              model: form.value,
              rules: rules.value,
              onSubmit: _withModifiers(generateBaoCao, ["prevent"])
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_row, {
                  gutter: 10,
                  align: "bottom",
                  class: "mb-4"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_col, {
                      lg: 3,
                      md: 8,
                      sm: 12,
                      xl: 3,
                      xs: 24
                    }, {
                      default: _withCtx(() => [
                        _createVNode(LabelCustom, {
                          "is-text-muted": false,
                          "is-required": true
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode("Từ tháng:")
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_el_form_item, {
                          prop: "from",
                          class: "mb-0"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_date_picker, {
                              modelValue: form.value.from,
                              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.value.from) = $event)),
                              class: "w-100",
                              "disabled-date": (time) => _unref(disableDate)(time, +currentYear.value, true),
                              type: "month",
                              placeholder: "Từ",
                              name: "from",
                              format: "MM/YYYY"
                            }, null, 8, ["modelValue", "disabled-date"])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_col, {
                      lg: 3,
                      md: 8,
                      sm: 12,
                      xl: 3,
                      xs: 24
                    }, {
                      default: _withCtx(() => [
                        _createVNode(LabelCustom, {
                          "is-text-muted": false,
                          "is-required": true
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode("Đến tháng:")
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_el_form_item, {
                          prop: "to",
                          class: "mb-0"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_date_picker, {
                              modelValue: form.value.to,
                              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.value.to) = $event)),
                              class: "w-100",
                              "disabled-date": (time) => _unref(disableDate)(time, +currentYear.value, true),
                              type: "month",
                              placeholder: "Đến",
                              name: "to",
                              format: "MM/YYYY"
                            }, null, 8, ["modelValue", "disabled-date"])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_col, {
                      lg: 5,
                      md: 8,
                      sm: 12,
                      xl: 5,
                      xs: 24
                    }, {
                      default: _withCtx(() => [
                        _createVNode(LabelCustom, {
                          "is-text-muted": false,
                          "is-required": true
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode("Nhóm vật tư:")
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_el_form_item, {
                          prop: "id_danh_muc_vat_tu",
                          class: "mb-0"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_select, {
                              modelValue: form.value.id_danh_muc_vat_tu,
                              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((form.value.id_danh_muc_vat_tu) = $event)),
                              class: "w-100",
                              clearable: "",
                              "default-first-option": "",
                              filterable: "",
                              placeholder: "Chọn nhóm vật tư",
                              remote: "",
                              loading: searchingDm.value,
                              "loading-text": "Đang tìm kiếm",
                              "no-data-text": "Không tìm thấy nhóm vật tư",
                              "remote-method": searchDm,
                              onClear: _cache[4] || (_cache[4] = ($event: any) => (form.value.id_danh_muc_vat_tu = undefined))
                            }, {
                              default: _withCtx(() => [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(dmOptions.value, (dm) => {
                                  return (_openBlock(), _createBlock(_component_el_option, {
                                    key: dm.id,
                                    label: dm.ten_danh_muc,
                                    value: dm.id
                                  }, null, 8, ["label", "value"]))
                                }), 128))
                              ]),
                              _: 1
                            }, 8, ["modelValue", "loading"])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_col, {
                      lg: 7,
                      md: 8,
                      sm: 12,
                      xl: 6,
                      xs: 24
                    }, {
                      default: _withCtx(() => [
                        _createVNode(LabelCustom, { "is-text-muted": false }, {
                          default: _withCtx(() => [
                            _createTextVNode("Tìm kiếm:")
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_el_form_item, { class: "mb-0" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_input, {
                              modelValue: _unref(searchTerm),
                              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => (_isRef(searchTerm) ? (searchTerm).value = $event : null)),
                              class: "w-100",
                              placeholder: "Mã hoặc tên vật tư",
                              clearable: ""
                            }, null, 8, ["modelValue"])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_col, {
                      lg: 6,
                      md: 8,
                      sm: 12,
                      xl: 7,
                      xs: 24
                    }, {
                      default: _withCtx(() => [
                        _createVNode(LabelCustom, {
                          "is-text-muted": false,
                          class: "nhap-kho-phu-tung-hide-zero-label",
                          onClick: _cache[6] || (_cache[6] = ($event: any) => (form.value.hideZeroActivity = !form.value.hideZeroActivity))
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(" Chỉ lấy vật tư có phát sinh: ")
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_el_form_item, { class: "mb-0" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_checkbox, {
                              modelValue: form.value.hideZeroActivity,
                              "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((form.value.hideZeroActivity) = $event))
                            }, null, 8, ["modelValue"])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(DivRow, { class: "mb-4" }, {
                  default: _withCtx(() => [
                    _createVNode(DivCol, { cols: 6 }),
                    _createVNode(DivCol, {
                      cols: 6,
                      class: "d-flex justify-content-end"
                    }, {
                      default: _withCtx(() => [
                        (showPrintButton.value)
                          ? (_openBlock(), _createBlock(ButtonCustom, {
                              key: 0,
                              "button-type": _unref(ButtonsType).PRINT,
                              onClick: openPdfViewer
                            }, {
                              "button-label": _withCtx(() => [
                                _createTextVNode("In phiếu")
                              ]),
                              _: 1
                            }, 8, ["button-type"]))
                          : _createCommentVNode("", true),
                        (showPrintButton.value)
                          ? (_openBlock(), _createBlock(ButtonCustom, {
                              key: 1,
                              "button-type": _unref(ButtonsType).CLOUD_DOWNLOAD,
                              "color-type": _unref(ButtonTypeColors).WARNING,
                              "tooltip-message": "Xuất Excel",
                              onClick: exportExcel
                            }, {
                              "button-label": _withCtx(() => [
                                _createTextVNode("Xuất Excel")
                              ]),
                              _: 1
                            }, 8, ["button-type", "color-type"]))
                          : _createCommentVNode("", true),
                        _createVNode(ButtonCustom, {
                          "button-type": _unref(ButtonsType).FA_FILE_EXPORT_THIN,
                          "color-type": _unref(ButtonTypeColors).SUCCESS,
                          "tooltip-message": "Xuất báo cáo",
                          onClick: generateBaoCao
                        }, null, 8, ["button-type", "color-type"])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["model", "rules"]),
            (!isFiltersChanging.value)
              ? (_openBlock(), _createBlock(CompanyReportTableWrapper, { key: 0 }, {
                  default: _withCtx(() => [
                    _createVNode(NhapKhoVatTuPhuTungTable, {
                      "is-print-modal": false,
                      items: items.value,
                      "don-vi": reportMeta.value?.don_vi ?? [],
                      stripe: true
                    }, null, 8, ["items", "don-vi"])
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (!isFiltersChanging.value && supportsPagination.value && _unref(totalItems) > 0)
              ? (_openBlock(), _createBlock(Pagination, {
                  key: 1,
                  page: _unref(currentPage),
                  "page-size": _unref(perPage),
                  total: _unref(totalItems),
                  "show-per-page": false,
                  onChangePage: onPageChange
                }, null, 8, ["page", "page-size", "total"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        })
      ]),
      _: 1
    })
  ], 64))
}
}

})